import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useTableFilters } from '@/components/data-table/filters-v2';
import { useCustomAttributes } from '@/modules/team/hooks/use-custom-attributes';

import { getAvailableColumns } from '../api/reports.api';
import { DIMENSION_KEY_PROJECT, DIMENSION_KEY_USER } from '../constants/report-dimension-keys';
import { REPORT_AVAILABLE_COLUMNS_QUERY_KEY } from '../constants/reports-query-keys';
import { getReportFiltersConfig } from '../helpers/report-filters-config';
import { mapReportFilters } from '../mappers/report-filters.mapper';
import { Report } from '../types';
import { ReportFilterFieldDTO } from '../types/report-filter-field';

const COLUMNS_STALE_TIME_MS = 10 * 60 * 1000;

/**
 * Report filters: the grouping-driven inline Project / Member pickers plus the "Filter"
 * dropdown with every other filter the report's data sources can apply. Dropdown values
 * live in the URL, so a filtered report can be shared by link.
 */
export const useReportFilters = (report: Report | undefined) => {
  const { t } = useTranslation('authenticated.reports');

  const elements = useMemo(() => report?.elements ?? [], [report]);
  const reportTypes = useMemo(
    () => [...new Set(elements.map(el => el.reportType).filter((type): type is string => !!type))],
    [elements]
  );

  const columnsQueries = useQueries({
    queries: reportTypes.map(type => ({
      queryKey: REPORT_AVAILABLE_COLUMNS_QUERY_KEY(type),
      queryFn: () => getAvailableColumns(type),
      staleTime: COLUMNS_STALE_TIME_MS,
    })),
  });

  const availableFieldsKey = columnsQueries
    .flatMap(q => q.data?.filters ?? [])
    .sort()
    .join(',');
  const availableFields = useMemo(
    () =>
      new Set(availableFieldsKey ? (availableFieldsKey.split(',') as ReportFilterFieldDTO[]) : []),
    [availableFieldsKey]
  );

  // Grouping by project / member keeps showing the inline picker for it (pre-existing behaviour)
  const groupKeys = useMemo(
    () =>
      new Set(
        elements.flatMap(el => [...el.groupBy, ...(el.pivotDimension ? [el.pivotDimension] : [])])
      ),
    [elements]
  );
  const hasProjectDimension = groupKeys.has(DIMENSION_KEY_PROJECT);
  const hasMemberDimension = groupKeys.has(DIMENSION_KEY_USER);
  const inlineFields = useMemo(
    () =>
      new Set<ReportFilterFieldDTO>([
        ...(hasProjectDimension ? [ReportFilterFieldDTO.PROJECT] : []),
        ...(hasMemberDimension ? [ReportFilterFieldDTO.MEMBER] : []),
      ]),
    [hasProjectDimension, hasMemberDimension]
  );

  const { data: customAttributes } = useCustomAttributes({
    enabled: availableFields.has(ReportFilterFieldDTO.MEMBER_CUSTOM_ATTRIBUTE),
  });

  const filtersConfig = useMemo(
    () => getReportFiltersConfig({ availableFields, inlineFields, customAttributes }),
    // `t` rebuilds the config on language change — getReportFiltersConfig reads i18n directly
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [availableFields, inlineFields, customAttributes, t]
  );

  const filterState = useTableFilters(filtersConfig);
  const requestFilters = useMemo(
    () => mapReportFilters(filterState.activeFilters),
    [filterState.activeFilters]
  );

  return {
    filtersConfig,
    filterState,
    requestFilters,
    hasProjectDimension,
    hasMemberDimension,
  };
};
